import React, { useState, useEffect } from "react";
import "@/App.css";
import Header from "@/components/Header";
import HeroSplit from "@/components/HeroSplit";
import TrustIndicators from "@/components/TrustIndicators";
import Benefits from "@/components/Benefits";
import ROIReporting from "@/components/ROIReporting";
import Services from "@/components/Services";
import Testimonials from "@/components/Testimonials";
import FAQ from "@/components/FAQ";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import Blog from "@/components/Blog";
import BlogPost from "@/components/BlogPost";
import BlogAdmin from "@/components/BlogAdmin";
import CaseStudies from "@/components/CaseStudies";
import CaseStudyDetail from "@/components/CaseStudyDetail";
import CaseStudiesAdmin from "@/components/CaseStudiesAdmin";
import Resources from "@/components/Resources";
import ResourcesAdmin from "@/components/ResourcesAdmin";
import PartnersAdmin from "@/components/PartnersAdmin";
import Careers from "@/components/Careers";
import CareersAdmin from "@/components/CareersAdmin";
import Coaches from "@/components/Coaches";
import CoachesAdmin from "@/components/CoachesAdmin";
import PrivacyPolicy from "@/components/PrivacyPolicy";
import TermsOfService from "@/components/TermsOfService";
import ROICalculator from "@/components/ROICalculatorRefactored";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";
import EmailTemplateAdmin from "@/components/EmailTemplateAdmin";
import ServicesAdmin from "@/components/ServicesAdmin";
import SubscriptionsAdmin from "@/components/admin/SubscriptionsAdmin";
import LegacyUsageMonitor from "@/components/LegacyUsageMonitor";
import AboutUs from "@/components/AboutUs";
import Shop from "@/components/Shop";
import ResourceDetail from "@/components/ResourceDetail";
import Checkout from "@/components/Checkout";
import { PaymentSuccess, PaymentFailed } from "@/components/checkout/PaymentViews";
import FiveDShowcase from "@/components/FiveDShowcase";
import FiveDMethodology from "@/components/FiveDMethodology";
import B2BValueSection from "@/components/B2BValueSection";
import B2CValueSection from "@/components/B2CValueSection";
import AudienceSelectorModal from "@/components/AudienceSelectorModal";
import AudienceBasedSections from "@/components/AudienceBasedSections";
import ABTestAdmin from "@/components/ABTestAdmin";
import SubscriberModal from "@/components/SubscriberModal";
import SubscribersAdmin from "@/components/SubscribersAdmin";
import DiscountCodesAdmin from "@/components/DiscountCodesAdmin";
import { AudienceProvider } from "@/context/AudienceContext";
import { ABTestProvider } from "@/context/ABTestContext";
import { Toaster } from "@/components/ui/sonner";
import { URL_ROUTES, VIEW_TO_URL, navigateTo } from "@/utils/routes";

function App() {
  const [currentView, setCurrentView] = useState('home');
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [selectedCaseStudyId, setSelectedCaseStudyId] = useState(null);
  const [selectedResourceId, setSelectedResourceId] = useState(null);
  const [showROICalculator, setShowROICalculator] = useState(false);

  // Handle URL-based routing on initial load
  useEffect(() => {
    const rawPath = window.location.pathname;
    // Strip trailing slash (except for root) so /resources/ matches /resources
    const path = rawPath !== '/' ? rawPath.replace(/\/+$/, '') : rawPath;
    const params = new URLSearchParams(window.location.search);
    
    // Auto-set audience from query param (e.g., from email links)
    const audienceParam = params.get('audience');
    if (audienceParam === 'individual' || audienceParam === 'corporate') {
      localStorage.setItem('coachmeupp_audience', audienceParam);
    }
    
    if (URL_ROUTES[path]) {
      setCurrentView(URL_ROUTES[path]);
    } else if (path.startsWith('/blog/')) {
      const postId = path.replace('/blog/', '');
      setSelectedPostId(postId);
      setCurrentView('post');
    } else if (path.startsWith('/case-studies/')) {
      const caseStudyId = path.replace('/case-studies/', '');
      setSelectedCaseStudyId(caseStudyId);
      setCurrentView('caseStudyDetail');
    } else if (path.startsWith('/resources/')) {
      const rid = path.replace('/resources/', '');
      if (rid) {
        setSelectedResourceId(rid);
        setCurrentView('resourceDetail');
      }
    }
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state?.view) {
        setCurrentView(event.state.view);
      } else {
        const path = window.location.pathname;
        if (URL_ROUTES[path]) {
          setCurrentView(URL_ROUTES[path]);
        } else if (path === '/') {
          setCurrentView('home');
        }
      }
      window.scrollTo({ top: 0 });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);


  const handleBlogClick = () => navigateTo(setCurrentView, 'blog');
  const handleAdminClick = () => navigateTo(setCurrentView, 'admin');

  const handlePostClick = (postId) => {
    setSelectedPostId(postId);
    navigateTo(setCurrentView, 'post');
  };

  const handleBackToBlog = () => navigateTo(setCurrentView, 'blog');
  const handleBackToHome = () => navigateTo(setCurrentView, 'home');
  const handleCaseStudiesClick = () => navigateTo(setCurrentView, 'caseStudies');

  const handleCaseStudyClick = (studyId) => {
    setSelectedCaseStudyId(studyId);
    navigateTo(setCurrentView, 'caseStudyDetail');
  };

  const handleBackToCaseStudies = () => navigateTo(setCurrentView, 'caseStudies');
  const handleResourcesClick = () => navigateTo(setCurrentView, 'resources');
  const handleResourcesAdminClick = () => navigateTo(setCurrentView, 'resourcesAdmin');
  const handleResourceClick = (resourceId) => {
    setSelectedResourceId(resourceId);
    setCurrentView('resourceDetail');
    window.history.pushState({ view: 'resourceDetail', resourceId }, '', `/resources/${resourceId}`);
    window.scrollTo({ top: 0 });
  };
  const handleCareersClick = () => navigateTo(setCurrentView, 'careers');
  const handleCareersAdminClick = () => navigateTo(setCurrentView, 'careersAdmin');
  const handleCoachesClick = () => navigateTo(setCurrentView, 'coaches');
  const handleCoachesAdminClick = () => navigateTo(setCurrentView, 'coachesAdmin');
  const handleCaseStudiesAdminClick = () => navigateTo(setCurrentView, 'caseStudiesAdmin');
  const handleAnalyticsClick = () => navigateTo(setCurrentView, 'analytics');
  const handleEmailTemplatesClick = () => navigateTo(setCurrentView, 'emailTemplates');
  const handlePrivacyClick = () => navigateTo(setCurrentView, 'privacy');
  const handleTermsClick = () => navigateTo(setCurrentView, 'terms');
  const handlePartnersAdminClick = () => navigateTo(setCurrentView, 'partnersAdmin');
  const handleShopClick = () => navigateTo(setCurrentView, 'shop');
  const handleCheckoutClick = () => navigateTo(setCurrentView, 'checkout');
  const handleServicesAdminClick = () => navigateTo(setCurrentView, 'servicesAdmin');
  const handleSubscriptionsAdminClick = () => navigateTo(setCurrentView, 'subscriptionsAdmin');
  const handleMethodologyClick = () => navigateTo(setCurrentView, 'methodology');
  const handleAboutClick = () => navigateTo(setCurrentView, 'aboutUs');

  // Admin view (legacy URL)
  if (currentView === 'admin') {
    return (
      <div className="App">
        <BlogAdmin onBackToHome={handleBackToHome} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Blog Admin view
  if (currentView === 'blogAdmin') {
    return (
      <div className="App">
        <BlogAdmin onBackToHome={handleBackToHome} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Case Studies Admin view
  if (currentView === 'caseStudiesAdmin') {
    return (
      <div className="App">
        <CaseStudiesAdmin onBackToHome={handleBackToHome} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Resources Admin view
  if (currentView === 'resourcesAdmin') {
    return (
      <div className="App">
        <ResourcesAdmin onBackToHome={handleBackToHome} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Partners Admin view
  if (currentView === 'partnersAdmin') {
    return (
      <div className="App">
        <PartnersAdmin onBackToHome={handleBackToHome} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Analytics Dashboard view
  if (currentView === 'analytics') {
    return (
      <div className="App">
        <AnalyticsDashboard onBackToHome={handleBackToHome} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Email Templates Admin view
  if (currentView === 'emailTemplates') {
    return (
      <div className="App">
        <EmailTemplateAdmin onBackToHome={handleBackToHome} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Services Admin view
  if (currentView === 'servicesAdmin') {
    return (
        <div className="App">
          <ServicesAdmin onBackToHome={handleBackToHome} />
          <Toaster position="top-right" />
        </div>
    );
  }

  // Subscriptions Admin view
  if (currentView === 'subscriptionsAdmin') {
    return (
      <div className="App">
        <SubscriptionsAdmin onBack={handleBackToHome} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // A/B Test Admin view
  if (currentView === 'abTestAdmin') {
    return (
      <ABTestProvider>
        <div className="App">
          <ABTestAdmin onBack={handleBackToHome} />
          <Toaster position="top-right" />
        </div>
      </ABTestProvider>
    );
  }

  // Subscribers Admin view
  if (currentView === 'subscribersAdmin') {
    return (
      <div className="App">
        <SubscribersAdmin onBackToHome={handleBackToHome} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Discount Codes Admin view
  if (currentView === 'discountCodesAdmin') {
    return (
      <div className="App">
        <DiscountCodesAdmin onBackToHome={handleBackToHome} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // About Us view
  if (currentView === 'aboutUs') {
    return (
      <div className="App">
        <AboutUs onNavigate={(section) => {
          if (section === 'contact') {
            setCurrentView('home');
            setTimeout(() => {
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
            }, 300);
          }
        }} onBackToHome={handleBackToHome} />
        <Footer 
          onBlogClick={handleBlogClick}
          onCaseStudiesClick={handleCaseStudiesClick}
          onResourcesClick={handleResourcesClick}
          onCareersClick={handleCareersClick}
          onPrivacyClick={handlePrivacyClick}
          onTermsClick={handleTermsClick}
          onCoachesClick={handleCoachesClick}
          onAboutClick={handleAboutClick}
        />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Legacy Usage Monitor view
  if (currentView === 'legacyUsage') {
    return (
      <div className="App">
        <LegacyUsageMonitor onBackToHome={handleBackToHome} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Shop view
  if (currentView === 'shop') {
    return (
      <AudienceProvider>
        <ABTestProvider>
            <div className="App">
              <Shop 
                onBackToHome={handleBackToHome} 
                onCheckout={handleCheckoutClick}
              />
              <AudienceSelectorModal />
              <Toaster position="top-right" />
            </div>
        </ABTestProvider>
      </AudienceProvider>
    );
  }

  // Checkout view
  if (currentView === 'checkout') {
    return (
      <AudienceProvider>
          <div className="App">
            <Checkout 
              onBackToShop={handleShopClick}
              onBackToHome={handleBackToHome}
            />
            <Toaster position="top-right" />
          </div>
      </AudienceProvider>
    );
  }

  // Payment Success view — returned to from PayTR after a successful charge
  if (currentView === 'paymentSuccess') {
    const orderId = new URLSearchParams(window.location.search).get('order_id') || '';
    return (
      <div className="App">
        <PaymentSuccess orderId={orderId} onBackToHome={handleBackToHome} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Payment Failure view — returned to from PayTR after a declined/failed charge
  if (currentView === 'paymentFailure') {
    const orderId = new URLSearchParams(window.location.search).get('order_id') || '';
    return (
      <div className="App">
        <PaymentFailed
          orderId={orderId}
          onRetry={() => navigateTo(setCurrentView, 'checkout')}
          onBackToHome={handleBackToHome}
        />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Blog view
  if (currentView === 'blog') {
    return (
      <div className="App">
        <Blog 
          onPostClick={handlePostClick} 
          onBackToHome={handleBackToHome}
          onAdminClick={handleAdminClick}
        />
        <Footer 
          onBlogClick={handleBlogClick} 
          onCaseStudiesClick={handleCaseStudiesClick}
          onResourcesClick={handleResourcesClick}
          onCareersClick={handleCareersClick}
          onPrivacyClick={handlePrivacyClick}
          onTermsClick={handleTermsClick}
          onCoachesClick={handleCoachesClick}
        />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Blog post view
  if (currentView === 'post') {
    return (
      <div className="App">
        <BlogPost 
          postId={selectedPostId}
          onBack={handleBackToBlog}
          onBackToHome={handleBackToHome}
        />
        <Footer 
          onBlogClick={handleBlogClick}
          onCaseStudiesClick={handleCaseStudiesClick}
          onResourcesClick={handleResourcesClick}
          onCareersClick={handleCareersClick}
          onPrivacyClick={handlePrivacyClick}
          onTermsClick={handleTermsClick}
          onCoachesClick={handleCoachesClick}
        />
        <Toaster position="top-right" />
      </div>
    );
  }

  // 5D Methodology page view
  if (currentView === 'methodology') {
    return (
      <div className="App">
        <FiveDMethodology 
          onBackToHome={handleBackToHome}
          onBlogClick={handleBlogClick}
          onCaseStudiesClick={handleCaseStudiesClick}
          onResourcesClick={handleResourcesClick}
          onCareersClick={handleCareersClick}
          onCoachesClick={handleCoachesClick}
          onPrivacyClick={handlePrivacyClick}
          onTermsClick={handleTermsClick}
          onShopClick={handleShopClick}
        />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Case Studies view
  if (currentView === 'caseStudies') {
    return (
      <div className="App">
        <CaseStudies 
          onCaseStudyClick={handleCaseStudyClick}
          onBackToHome={handleBackToHome}
          onAdminClick={handleCaseStudiesAdminClick}
        />
        <Footer 
          onBlogClick={handleBlogClick}
          onCaseStudiesClick={handleCaseStudiesClick}
          onResourcesClick={handleResourcesClick}
          onCareersClick={handleCareersClick}
          onPrivacyClick={handlePrivacyClick}
          onTermsClick={handleTermsClick}
          onCoachesClick={handleCoachesClick}
        />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Case Study Detail view
  if (currentView === 'caseStudyDetail') {
    return (
      <div className="App">
        <CaseStudyDetail 
          studyId={selectedCaseStudyId}
          onBack={handleBackToCaseStudies}
          onBackToHome={handleBackToHome}
        />
        <Footer 
          onBlogClick={handleBlogClick}
          onCaseStudiesClick={handleCaseStudiesClick}
          onResourcesClick={handleResourcesClick}
          onCareersClick={handleCareersClick}
          onPrivacyClick={handlePrivacyClick}
          onTermsClick={handleTermsClick}
          onCoachesClick={handleCoachesClick}
        />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Resources view
  if (currentView === 'resources') {
    return (
      <div className="App">
        <Resources 
          onBackToHome={handleBackToHome}
          onAdminClick={handleResourcesAdminClick}
          onResourceClick={handleResourceClick}
        />
        <Footer 
          onBlogClick={handleBlogClick}
          onCaseStudiesClick={handleCaseStudiesClick}
          onResourcesClick={handleResourcesClick}
          onCareersClick={handleCareersClick}
          onPrivacyClick={handlePrivacyClick}
          onTermsClick={handleTermsClick}
          onCoachesClick={handleCoachesClick}
        />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Resource Detail view (CoachHub-style landing page)
  if (currentView === 'resourceDetail') {
    return (
      <div className="App">
        <ResourceDetail
          resourceId={selectedResourceId}
          onBack={handleResourcesClick}
        />
        <Footer 
          onBlogClick={handleBlogClick}
          onCaseStudiesClick={handleCaseStudiesClick}
          onResourcesClick={handleResourcesClick}
          onCareersClick={handleCareersClick}
          onPrivacyClick={handlePrivacyClick}
          onTermsClick={handleTermsClick}
          onCoachesClick={handleCoachesClick}
        />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Careers view
  if (currentView === 'careers') {
    return (
      <div className="App">
        <Careers 
          onBackToHome={handleBackToHome}
          onAdminClick={handleCareersAdminClick}
        />
        <Footer 
          onBlogClick={handleBlogClick}
          onCaseStudiesClick={handleCaseStudiesClick}
          onResourcesClick={handleResourcesClick}
          onCareersClick={handleCareersClick}
          onPrivacyClick={handlePrivacyClick}
          onTermsClick={handleTermsClick}
          onCoachesClick={handleCoachesClick}
        />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Careers Admin view
  if (currentView === 'careersAdmin') {
    return (
      <div className="App">
        <CareersAdmin onBackToHome={handleBackToHome} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Coaches view
  if (currentView === 'coaches') {
    return (
      <div className="App">
        <Coaches 
          onBackToHome={handleBackToHome}
          onAdminClick={handleCoachesAdminClick}
        />
        <Footer 
          onBlogClick={handleBlogClick}
          onCaseStudiesClick={handleCaseStudiesClick}
          onResourcesClick={handleResourcesClick}
          onCareersClick={handleCareersClick}
          onPrivacyClick={handlePrivacyClick}
          onTermsClick={handleTermsClick}
          onCoachesClick={handleCoachesClick}
        />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Coaches Admin view
  if (currentView === 'coachesAdmin') {
    return (
      <div className="App">
        <CoachesAdmin onBackToHome={handleBackToHome} />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Privacy Policy view
  if (currentView === 'privacy') {
    return (
      <div className="App">
        <PrivacyPolicy onBackToHome={handleBackToHome} />
        <Footer 
          onBlogClick={handleBlogClick}
          onCaseStudiesClick={handleCaseStudiesClick}
          onResourcesClick={handleResourcesClick}
          onCareersClick={handleCareersClick}
          onPrivacyClick={handlePrivacyClick}
          onTermsClick={handleTermsClick}
          onCoachesClick={handleCoachesClick}
        />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Terms of Service view
  if (currentView === 'terms') {
    return (
      <div className="App">
        <TermsOfService onBackToHome={handleBackToHome} />
        <Footer 
          onBlogClick={handleBlogClick}
          onCaseStudiesClick={handleCaseStudiesClick}
          onResourcesClick={handleResourcesClick}
          onCareersClick={handleCareersClick}
          onPrivacyClick={handlePrivacyClick}
          onTermsClick={handleTermsClick}
          onCoachesClick={handleCoachesClick}
        />
        <Toaster position="top-right" />
      </div>
    );
  }

  // Home view
  return (
    <AudienceProvider>
      <ABTestProvider>
          <div className="App">
            <Header 
              onBlogClick={handleBlogClick}
              onCaseStudiesClick={handleCaseStudiesClick}
              onResourcesClick={handleResourcesClick}
              onROICalculatorClick={() => setShowROICalculator(true)}
              onShopClick={handleShopClick}
              onMethodologyClick={handleMethodologyClick}
              onAboutClick={handleAboutClick}
            />
            <main>
              <HeroSplit 
                onROICalculatorClick={() => setShowROICalculator(true)} 
                onShopClick={(type) => {
                  if (type === 'individual') {
                    setCurrentView('shop');
                    window.scrollTo({ top: 0 });
                  } else {
                    handleShopClick();
                  }
                }}
              />
              <B2BValueSection 
                onContactClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                onROICalculatorClick={() => setShowROICalculator(true)}
                onCaseStudiesClick={handleCaseStudiesClick}
              />
              <B2CValueSection 
                onShopClick={(type) => {
                  setCurrentView('shop');
                  window.scrollTo({ top: 0 });
                }}
                onCoachesClick={handleCoachesClick}
              />
              <FiveDShowcase onViewMethodology={handleMethodologyClick} />
              <AudienceBasedSections onROICalculatorClick={() => setShowROICalculator(true)} />
              <Testimonials />
              <FAQ />
              <Contact />
            </main>
            <Footer 
              onBlogClick={handleBlogClick}
              onCaseStudiesClick={handleCaseStudiesClick}
              onResourcesClick={handleResourcesClick}
              onCareersClick={handleCareersClick}
              onPrivacyClick={handlePrivacyClick}
              onTermsClick={handleTermsClick}
              onCoachesClick={handleCoachesClick}
              onAnalyticsClick={handleAnalyticsClick}
              onEmailTemplatesClick={handleEmailTemplatesClick}
              onPartnersAdminClick={handlePartnersAdminClick}
              onServicesAdminClick={handleServicesAdminClick}
              onSubscriptionsAdminClick={handleSubscriptionsAdminClick}
              onShopClick={handleShopClick}
              onMethodologyClick={handleMethodologyClick}
              onAboutClick={handleAboutClick}
              onABTestAdminClick={() => { setCurrentView('abTestAdmin'); window.scrollTo({ top: 0 }); }}
              onDiscountCodesAdminClick={() => { navigateTo(setCurrentView, 'discountCodesAdmin'); }}
            />
            
            {/* Audience Selector Modal */}
            <AudienceSelectorModal />
            
            {/* Subscriber Popup */}
            <SubscriberModal />
            
            {/* ROI Calculator Modal */}
            <ROICalculator 
              isOpen={showROICalculator} 
              onClose={() => setShowROICalculator(false)} 
            />
            
            <Toaster position="top-right" />
          </div>
      </ABTestProvider>
    </AudienceProvider>
  );
}

export default App;
